import { DestroyRef, EventEmitter, inject, Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { isPlatformBrowser, isPlatformServer, Location } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Constants } from '@helper/constants';
import { RouteHelper, RoutePaths } from '@helper/route-paths';
import { LativLocationService } from '@service/lativ-location.service';
import { LoadMoreService } from '@service/load-more.service';
import { EventTracker } from '../analytics/event-tracker.service';

export class RouterHistory {
  constructor(
    public path: string,
    public scrollTop: number
  ) {}
}

@Injectable({
  providedIn: 'root',
})
export class RouterHistoryService {
  private get scrollTop(): number {
    return isPlatformBrowser(this.platformId) ? window.scrollY : 0;
  }

  readonly restoreScrollTop = new EventEmitter<number>();
  readonly eventTracker = inject(EventTracker);

  private history: RouterHistory[] = [];
  private isFirstLoad = false;
  private triggerByHistoryGoN = false;
  private triggerByHistoryBack = false;
  private triggerByHistoryForward = false;
  private firstLoadPages = [
    `/${RoutePaths.SALES_EVENT}`,
    `/${RoutePaths.SEARCH}`,
    `/${RoutePaths.OUTFIT}`,
    `/${RoutePaths.PRODUCT}`,
    `/${RoutePaths.SPECIAL}`,
  ];

  constructor(
    private router: Router,
    private location: Location,
    private loadMoreService: LoadMoreService,
    @Inject(PLATFORM_ID) private platformId: object
  ) {}

  monitor(ref: DestroyRef): void {
    // monitor method should only call in app component onInit.
    this.isFirstLoad = true;
    this.router.events.pipe(takeUntilDestroyed(ref)).subscribe(event => {
      if (event instanceof NavigationStart) {
        // NavigationStart 是在路由導頁開始前觸發的，用來通知導頁即將發生.
        if (event.navigationTrigger === 'popstate' && !this.triggerByHistoryGoN) {
          // popstate, 表示觸發 NavigationStart 的路由, 是來自 history.back() 或 forward.
          // 為了只有 back 才使用, 判斷了 history 上一頁的路徑.
          // 因為 history 變數還沒 pop 所以要拿到上一頁的 URL 要 -2.
          // 備註: history.go(-n) 也會觸發 popstate, 但由 NavigationEnd 透過 triggerByHistoryGoN 變數處理.
          this.triggerByHistoryBack = this.history[this.history.length - 2]?.path === event.url;
          this.triggerByHistoryForward = !this.triggerByHistoryBack;
        }
        // tracker 需判斷上一次到商品頁的位置(如小分類、穿搭列表等等)
        this.eventTracker.setPageView(this.history[this.history.length - 1]?.path);
      } else if (event instanceof NavigationEnd) {
        // NavigationEnd 觸發順序: Component constructor -> NavigationEnd -> ngOnInit.
        if (this.isFirstLoad) {
          this.pushFirstHistoryHome();
        }
        this.isFirstLoad = false;
        if (this.triggerByHistoryGoN) {
          // 觸發來源: code trigger history.go(-n).
          // 處理: 因為這裡的 history, 已經在 goRoute, slice 掉.
          //      所以這裡跳過 push history 就好. 並且不管捲軸位置.
          this.triggerByHistoryGoN = false;
          return;
        }
        if (this.triggerByHistoryBack) {
          // 觸發來源: code or browser trigger history.back().
          // 處理:
          // 1. 跳過 push history.
          // 2. pop 一層 history.
          // 3. pop 一層後, 最後一個 history 就是當前頁面, 然後處理捲軸位置.
          this.triggerByHistoryBack = false;
          this.triggerByHistoryForward = false;
          this.history.pop();
          const current = this.history[this.history.length - 1];
          if (event.url === current?.path || !current) {
            this.restoreScrollTop.emit(current?.scrollTop ?? 0);
          }
          return;
        }
        // 不是往後路由觸發的 NavigationEnd, 就要塞歷史紀錄.
        // 但如果是 replace url 情況, 就直接替換最後一個 history.
        this.triggerByHistoryForward = false;
        const navigation = this.router.getCurrentNavigation();
        if (navigation?.trigger === 'imperative' && navigation?.extras?.replaceUrl) {
          this.replaceTopHistory(event.url);
          this.restoreScrollTop.emit(0);
        } else {
          this.pushHistory(event.url);
        }
      }
    });
    // 處理 replaceState, 它不會觸發路由, 但要更換最後一個 history 紀錄.
    (this.location as LativLocationService).locationReplaced.pipe(takeUntilDestroyed(ref)).subscribe(url => {
      if (!this.triggerByHistoryForward) {
        this.replaceTopHistory(url);
      }
    });
  }

  goHome(): void {
    this.loadMoreService.clearItemCount();
    this.goRoute(RouteHelper.home());
    this.restoreScrollTop.emit(0);
  }

  forceGoHome(): void {
    this.loadMoreService.clearItemCount();
    this.router.navigate([RouteHelper.home()]);
    this.restoreScrollTop.emit(0);
  }

  goRoute(route: string): void {
    // 備註：確定要 push history 往下走的頁面要, 不能使用goRoute.
    //      因為可能被 history 匹配到, 就會 history.go(-n).
    //---------------------------------------------------
    // 因為 push history 是用 NavigationEnd 的 url 開頭有帶斜線.
    // 所以 route 沒有斜線要補上, 要不然一定匹配不到.
    if (!route.startsWith('/')) {
      route = `/${route}`;
    }
    let goBackPosition = 0;
    const endPosition = this.history.length - 1;
    for (let i = endPosition; i >= 0; i--) {
      if (this.history[i].path === route) {
        goBackPosition = i - endPosition;
        break;
      }
    }
    // goBackPosition 會是負值.
    // 用來 history.go(goBackPosition)
    if (goBackPosition < -1) {
      // 往後超過一頁, 獨立處理, 不管捲軸位置.
      // 用變數 triggerByHistoryGoN 來判斷.
      // slice end index = length + (-n).
      this.history = this.history.slice(0, this.history.length + goBackPosition);
      this.triggerByHistoryGoN = true;
      // 若已經經過 popState 則 location history 已經 pop 過，會比 goBackPosition 還少一層 用 triggerByHistoryBack 判斷
      this.location.historyGo(goBackPosition + (this.triggerByHistoryBack ? 1 : 0));
    } else if (goBackPosition === -1) {
      // 往後一頁, 獨立處理, 因為處理捲軸位置.
      // 與瀏覽器 history.back 相同邏輯.
      // 使用 NavigationStart 的 popstate 判斷.
      this.location.historyGo(goBackPosition);
    } else {
      // 因使用 goRoute 是用來導往之前的頁面, 而當 history 沒有匹配目標路由紀錄.
      // 就會使用 navigate replaceUrl true. 處理
      // 它作用是讓欲前往的目標頁, 取代當下頁面的瀏覽器紀錄.
      // 例如: A->B->C. 在C頁使用 goRoute(B), 用 navigate replaceUrl.
      // 瀏覽器紀錄會變成 A->B->B (沒打錯字), 而不會是 A->B->C->B
      // 例如: A->B->C->D. 在D頁使用 goRoute(B).
      // 瀏覽器紀錄會變成 A->B->C->B, 而不會是 A->B->C->D->B
      // 而 replaceUrl true, 在 NavigationEnd 有處理,
      // 不會 push history. 而是 replace 最後一個 history 紀錄.
      this.router.navigate([route], { replaceUrl: true });
    }
  }

  goBack(): void {
    this.location.back();
  }

  getHistory(): RouterHistory[] {
    return this.history;
  }

  private pushFirstHistoryHome(): void {
    if (isPlatformServer(this.platformId)) {
      return;
    }
    // 瀏覽器不支援
    if (!window.performance) {
      return;
    }
    // 沒資料
    const firstEntry = window.performance.getEntries()[0];
    if (!firstEntry) {
      return;
    }
    // 嘗試過 preUnload, 或 destroy, 或 cookie 過期 或 history length.
    // 但都無法偵測是 reload 操作還是第一次從外部網站進來
    // 但發現 Performance reload 在 NavigationEnd 是 page reload.
    const navigateType = (firstEntry as PerformanceNavigationTiming).type;
    if (navigateType === 'reload') {
      return;
    }
    // 目前限定：活動頁、搜尋頁、商品明細頁、特輯頁、穿搭頁，為第一頁時，可以墊一層首頁.
    const pathName = window.location.pathname.toLowerCase();
    if (this.firstLoadPages.some(page => pathName.includes(page))) {
      // 把第一個用首頁，然後第二個用本頁，這樣 history back 就會是首頁
      const preUrl = window.location.href;
      window.history.replaceState('first', Constants.PAGE_TITLE, '/');
      window.history.pushState('second', Constants.PAGE_TITLE, preUrl);
    }
  }

  private pushHistory(url: string): void {
    const current = this.history[this.history.length - 1];
    if (current) {
      current.scrollTop = this.scrollTop;
    }
    if (this.history[this.history.length - 1]?.path !== url) {
      this.history.push(new RouterHistory(url, 0));
      this.restoreScrollTop.emit(0);
    }
  }

  private replaceTopHistory(url: string): void {
    this.history[Math.max(0, this.history.length - 1)] = new RouterHistory(url, this.scrollTop);
  }
}
